function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import find from 'lodash/find';
import get from 'lodash/get';
import includes from 'lodash/includes';
import filter from 'lodash/filter';
import some from 'lodash/some';
import { createSelector } from 'reselect';
import { getSlug } from 'helpers/urlTools';
import MetricConversions from 'libs/MetricConversions';
import { profile as profileCC } from 'modules/ContourCloud/selectors';
import { organizationMemberships, information } from 'modules/Information/selectors';
import { displayMode } from 'modules/App/selectors';
import * as actionTypes from './actionTypes';
import * as constants from './constants';
const EMPTY_ARRAY = [];
/**
 * Input selectors
 */ const mainSelector = (state)=>state[constants.MODULE_ID];
const accountSelector = (state)=>state[constants.MODULE_ID].account;
const fetchingSelector = (state)=>state[constants.MODULE_ID].fetching;
const sendingSelector = (state)=>state[constants.MODULE_ID].sending;
const errorsSelector = (state)=>state[constants.MODULE_ID].errors;
//----------------------------------------------------------------------------------------------------------------------
/**
 * Select isAuthenticated flag
 */ export const isAuthenticated = (state)=>mainSelector(state).isAuthenticated;
/**
 * Selects user's account data
 */ export const account = (state)=>accountSelector(state);
/**
 * Selects passphrase
 */ export const passphrase = (state)=>accountSelector(state).passphrase;
/**
 * Selects if user has a HCP account
 */ export const isHcpAccount = (state)=>accountSelector(state).scope === constants.SCOPE_NAMES.PROFESSIONAL;
/**
 * Selects if user has a PWD account
 */ export const isPatientAccount = (state)=>accountSelector(state).scope === constants.SCOPE_NAMES.PERSONAL;
/**
 * Selects if user has a CG account
 */ export const isCareGiverAccount = (state)=>accountSelector(state).scope === constants.SCOPE_NAMES.CAREGIVER;
export const isAccountScheduledForDeletion = (state)=>Boolean(accountSelector(state).isDeleted);
/**
 * Selects if user is a HCP
 */ export const isHcp = (state)=>Boolean(mainSelector(state)[constants.PROFILE_TYPES.HCP]);
/**
 * Selects if user is a Patient
 */ export const isPatient = (state)=>Boolean(mainSelector(state)[constants.PROFILE_TYPES.PWD]);
/**
 * Selects if user is a Care Giver
 */ export const isCareGiver = (state)=>Boolean(mainSelector(state)[constants.PROFILE_TYPES.CG]);
/**
 * Selects identity Wallet Entries list
 */ export const walletEntriesList = (state)=>mainSelector(state).walletEntriesList;
/**
 * Selects user's account settings
 */ export const settings = (state)=>accountSelector(state).settings;
/**
 * Selects user's glucoseLevelUnitTypes settings
 */ export const glucoseLevelUnitTypesSettings = (state)=>{
    var _settings;
    return (_settings = settings(state)) === null || _settings === void 0 ? void 0 : _settings.glucoseLevelUnitTypes;
};
/**
 * Selects all user's notifications settings even if already disabled in system
 */ export const allNotificationsSettings = (state)=>{
    var _settings;
    return (_settings = settings(state)) === null || _settings === void 0 ? void 0 : _settings.notifications;
};
/**
 * Selects active user's notifications settings
 */ export const activeNotificationsSettings = createSelector(allNotificationsSettings, (notifications)=>notifications && filter(notifications, ({ isActive })=>isActive === true));
/**
 * Selects user's main email
 */ export const email = (state)=>accountSelector(state).email;
/**
 * Selects user's prvKeyPem and pubKeyPem
 */ export const keyPair = (state)=>accountSelector(state).keyPair;
export const prvKeyPem = (state)=>{
    var _keyPair;
    return (_keyPair = keyPair(state)) === null || _keyPair === void 0 ? void 0 : _keyPair.prvKeyPem;
};
/**
 * Selects user's active profile type (HCP or PWD)
 */ export const activeProfileType = (state)=>mainSelector(state).activeProfileType;
/**
 * Selects user's active profile based on activeProfileType
 */ export const activeProfile = (state)=>mainSelector(state)[activeProfileType(state)];
/**
 * Selects user's hcp profile
 */ export const hcpProfile = (state)=>mainSelector(state)[constants.PROFILE_TYPES.HCP];
/**
 * Selects user's patient profile
 */ export const patientProfile = (state)=>mainSelector(state)[constants.PROFILE_TYPES.PWD];
/**
 * Selects user's caregiver profile
 */ export const cgProfile = (state)=>mainSelector(state)[constants.PROFILE_TYPES.CG];
/**
 * Selects extended user's patient profile
 */ export const patientProfileExtended = createSelector(patientProfile, information, (patientProfileState, informationState)=>_object_spread_props(_object_spread({}, patientProfileState), {
        avatar: get(informationState, 'avatar', ''),
        firstName: get(informationState, 'firstName', ''),
        lastName: get(informationState, 'lastName', ''),
        email: get(informationState, 'email', ''),
        dateOfBirth: get(informationState, 'dateOfBirth', '')
    }));
/**
 * Default settings for account country
 */ export const countrySettings = (state)=>mainSelector(state).countrySettings;
/**
 * Select custom patient identifiers dependent on country
 */ export const patientCustomIdentifiers = (state)=>mainSelector(state).patientCustomIdentifiers;
/**
 * Data from clinic Sharing Request to use in new PWD account creation
 */ export const patientDataFromInvitation = (state)=>mainSelector(state).patientDataFromInvitation;
/**
 * Health data from clinic Sharing Request to use in new PWD account creation
 */ export const patientHealthDataFromInvitation = (state)=>mainSelector(state).patientHealthDataFromInvitation;
/**
 * Select payers dependent on country
 */ export const payers = (state)=>mainSelector(state).payers;
/**
 * Select custom hcp identifiers dependent on country
 */ export const hcpCustomIdentifiers = (state)=>mainSelector(state).hcpCustomIdentifiers;
/**
 * Selects user's active clinic membership in professional clinic
 */ export const activeClinicMembershipId = (state)=>mainSelector(state).activeClinicMembershipId;
/**
 * Selects user's active organizationUID in professional clinic
 */ export const activeOrganizationUID = (state)=>mainSelector(state).activeOrganizationUID;
/**
 * Selects HCP's clinic memberships
 */ export const hcpClinicMemberships = (state)=>{
    var _hcpProfile;
    return ((_hcpProfile = hcpProfile(state)) === null || _hcpProfile === void 0 ? void 0 : _hcpProfile.clinicHcpMemberships) || EMPTY_ARRAY;
};
/**
 * Selects active HCP's clinic membership
 */ export const activeHcpClinicMemberships = createSelector([
    hcpClinicMemberships,
    activeClinicMembershipId
], (hcpClinicMembershipsState, activeClinicMembershipIdState)=>find(hcpClinicMembershipsState, ({ clinicHcpMembershipId })=>clinicHcpMembershipId === activeClinicMembershipIdState));
/**
 * Selects HCP's organization memberships that are active
 */ export const organizationMembershipsActive = createSelector(organizationMemberships, (organizationMembershipsState)=>filter(organizationMembershipsState, (organizationMembership)=>organizationMembership.status === 'Active' && organizationMembership.organization.status === 'Active'));
/**
 * Selects HCP's organization memberships that aren't active yet
 */ export const organizationMembershipsPending = createSelector(organizationMemberships, (organizationMembershipsState)=>filter(organizationMembershipsState, (organizationMembership)=>organizationMembership.status === 'Pending' || organizationMembership.organization.status === 'Pending'));
/**
 * Selects HCP's organization memberships that don't have a corresponding clinic
 */ export const organizationMembershipsWithoutClinics = createSelector(hcpClinicMemberships, organizationMembershipsActive, (hcpClinicMembershipsState, organizationMembershipsActiveState)=>filter(organizationMembershipsActiveState, (organizationMembership)=>!some(hcpClinicMembershipsState, (hcpClinicMembership)=>hcpClinicMembership.clinic.organizationUID === organizationMembership.organization.organizationUID)));
/**
 * Selects user's active clinic id based on active clinic membership
 */ export const activeClinicId = (state)=>{
    var _activeHcpClinicMemberships_clinic, _activeHcpClinicMemberships;
    return (_activeHcpClinicMemberships = activeHcpClinicMemberships(state)) === null || _activeHcpClinicMemberships === void 0 ? void 0 : (_activeHcpClinicMemberships_clinic = _activeHcpClinicMemberships.clinic) === null || _activeHcpClinicMemberships_clinic === void 0 ? void 0 : _activeHcpClinicMemberships_clinic.clinicId;
};
/**
 * Selects HCP's clinic memberships
 */ export const countryId = (state)=>accountSelector(state).countryId;
/**
 * Selects PWD's glucose level targets
 */ export const glucoseLevelTargets = (state)=>accountSelector(state).glucoseLevelTargets;
/**
 * Selects PWD's external data sources ids
 */ export const externalDataSourcesIds = (state)=>accountSelector(state).externalDataSourcesIds;
/**
 * Selects if Account should change the password
 */ export const changePasswordReset = (state)=>mainSelector(state).changePasswordReset;
/**
 * Selects Contour Cloud access token
 */ export const accountCCAccessToken = (state)=>mainSelector(state).accountCCAccessToken;
/**
 * Selects HCP's active clinic memberships
 */ export const activeClinicMembership = createSelector(activeClinicMembershipId, hcpClinicMemberships, (activeClinicMembershipIdState, hcpClinicMembershipsState)=>find(hcpClinicMembershipsState, (hcpClinicMembership)=>hcpClinicMembership.clinicHcpMembershipId === activeClinicMembershipIdState && hcpClinicMembership.membershipStatus !== 'Revoked'));
export const activeClinicMembershipPassphrase = (state)=>{
    var _activeClinicMembership;
    return (_activeClinicMembership = activeClinicMembership(state)) === null || _activeClinicMembership === void 0 ? void 0 : _activeClinicMembership.passphrase;
};
export const activeClinicMembershipPrvKeyPem = (state)=>{
    var _activeClinicMembership;
    return (_activeClinicMembership = activeClinicMembership(state)) === null || _activeClinicMembership === void 0 ? void 0 : _activeClinicMembership.encryptedPrivateKey;
};
/**
 * Selects HCP's active organization memberships
 */ export const activeOrganizationMembership = createSelector(activeOrganizationUID, organizationMembershipsPending, (activeOrganizationUIDState, organizationMembershipsPendingState)=>find(organizationMembershipsPendingState, (organizationMembership)=>organizationMembership.organization.organizationUID === activeOrganizationUIDState));
/**
 * Selects if user is clinic admin
 */ export const isClinicAdmin = createSelector(activeClinicMembership, (activeClinicMembershipState)=>{
    const { isAdmin, membershipStatus, clinic } = activeClinicMembershipState || {};
    return isAdmin && membershipStatus === 'Active' && clinic && [
        'Active',
        'Deleted',
        'WaitingForLicence'
    ].includes(clinic.clinicStatus);
});
/**
 * Returns UrlRoute object for hcp profile's main url
 */ export const hcpProfileMainUrlRoute = createSelector(isHcp, activeClinicMembership, activeOrganizationMembership, hcpClinicMemberships, organizationMembershipsWithoutClinics, changePasswordReset, (isHcpState, activeClinicMembershipState, activeOrganizationMembershipState, hcpClinicMembershipsState, organizationMembershipsWithoutClinicsState, changePasswordResetState)=>{
    if (changePasswordResetState) {
        return {
            name: 'set-new-password-after-reset'
        };
    }
    if (!isHcpState) {
        return {
            name: 'setup'
        };
    }
    if (organizationMembershipsWithoutClinicsState.length) {
        return {
            name: 'clinic-setup'
        };
    }
    const clinicMembership = activeClinicMembershipState || find(hcpClinicMembershipsState, (hcpClinicMembership)=>hcpClinicMembership.membershipStatus !== 'Revoked');
    const { organizationUID, name, storageProvider } = get(activeOrganizationMembershipState, 'organization') || get(clinicMembership, 'clinic', {});
    if (clinicMembership && clinicMembership.isAdmin && !storageProvider) {
        return {
            name: 'add-drive'
        };
    }
    return {
        name: 'general-population',
        params: {
            clinicSlug: getSlug(name),
            organizationUID
        },
        isFinal: true
    };
});
/**
 * Returns UrlRoute object for hcp patients's main url
 */ export const patientProfileMainUrlRoute = createSelector(patientProfile, profileCC, changePasswordReset, displayMode, (patientProfileState, profileCCState, changePasswordResetState, displayModeState)=>{
    if (changePasswordResetState) {
        return {
            name: 'set-new-password-after-reset'
        };
    }
    if (profileCCState && profileCCState.status === 'Inactive') {
        return {
            name: 'confirm-email-cc'
        };
    }
    if (!patientProfileState || !patientProfileState.encryptedExchangeToken && !patientProfileState.encryptedRefreshToken) {
        if (displayModeState === 'desktop') {
            return {
                name: 'setup',
                alternates: [
                    'google-drive'
                ]
            };
        }
        return {
            name: 'setup-mobile'
        };
    }
    if (displayModeState === 'mobile') {
        return {
            name: 'mobile-my-doctors',
            alternates: [
                'mobile-enroll-in-clinic',
                'mobile-visits-history',
                'mobile-visit-notes',
                'setup-mobile',
                'host-mobile',
                'callback-close-webview-mobile'
            ]
        };
    }
    return {
        name: 'my-results',
        isFinal: true
    };
});
/**
 * Returns UrlRoute object for hcp profile's main url
 */ export const cgProfileMainUrlRoute = createSelector(isCareGiver, (isCareGiverState)=>{
    if (!isCareGiverState) {
        return {
            name: 'setup'
        };
    }
    return {
        name: 'my-results',
        isFinal: true
    };
});
/**
 * Returns UrlRoute object for current profile's main url
 */ export const activeProfileMainUrlRoute = createSelector(activeProfileType, hcpProfileMainUrlRoute, patientProfileMainUrlRoute, cgProfileMainUrlRoute, (activeProfileTypeState, hcpUrlRoute, patientUrlRoute, cgUrlRoute)=>{
    switch(activeProfileTypeState){
        case constants.PROFILE_TYPES.HCP:
            return hcpUrlRoute;
        case constants.PROFILE_TYPES.PWD:
            return patientUrlRoute;
        case constants.PROFILE_TYPES.CG:
            return cgUrlRoute;
        // TODO: default is dangerous in that form
        default:
            return {
                name: 'sign-in'
            };
    }
});
export const metricsUnits = (state)=>mainSelector(state).metricsUnits;
export const metricConversions = createSelector([
    metricsUnits
], (metricsUnitsState)=>new MetricConversions(metricsUnitsState));
export const gdprData = (state)=>mainSelector(state).gdprData;
export const tokenExpired = (state)=>mainSelector(state).tokenExpired;
//----------------------------------------------------------------------------------------------------------------------
export const elevatedPermissionsMode = (state)=>mainSelector(state).elevatedPermissionsMode;
export const hasElevatedPermissions = (state)=>mainSelector(state).hasElevatedPermissions;
export const hasElevatePermissionsError = createSelector(errorsSelector, (state)=>includes(state, actionTypes.ELEVATE_PERMISSIONS));
//----------------------------------------------------------------------------------------------------------------------
export const isUpdateAccountSettingInProgress = createSelector(sendingSelector, (state)=>includes(state, actionTypes.UPDATE_ACCOUNT_SETTINGS));
export const hasUpdateAccountSettingsErrors = createSelector(errorsSelector, (state)=>includes(state, actionTypes.UPDATE_ACCOUNT_SETTINGS));
//----------------------------------------------------------------------------------------------------------------------
export const isDeleteAccountInProgress = createSelector(sendingSelector, (state)=>includes(state, actionTypes.DELETE_ACCOUNT));
export const hasDeleteAccountErrors = createSelector(errorsSelector, (state)=>includes(state, actionTypes.DELETE_ACCOUNT));
//----------------------------------------------------------------------------------------------------------------------
export const isUpdateHcpProfileInProgress = createSelector(sendingSelector, (state)=>includes(state, actionTypes.UPDATE_HCP_PROFILE));
export const hasUpdateHcpProfileErrors = createSelector(errorsSelector, (state)=>includes(state, actionTypes.UPDATE_HCP_PROFILE));
//----------------------------------------------------------------------------------------------------------------------
export const isUpdatePatientProfileInProgress = createSelector(sendingSelector, (state)=>includes(state, actionTypes.UPDATE_PATIENT_PROFILE));
export const hasUpdatePatientProfileErrors = createSelector(errorsSelector, (state)=>includes(state, actionTypes.UPDATE_PATIENT_PROFILE));
//----------------------------------------------------------------------------------------------------------------------
export const isUpdateCgProfileInProgress = createSelector(sendingSelector, (state)=>includes(state, actionTypes.UPDATE_CG_PROFILE));
export const hasUpdateCgProfileErrors = createSelector(errorsSelector, (state)=>includes(state, actionTypes.UPDATE_CG_PROFILE));
//----------------------------------------------------------------------------------------------------------------------
export const isReauthorizePatientProfileCloudDriveInProgress = createSelector(sendingSelector, (state)=>includes(state, actionTypes.REAUTHORIZE_PATIENT_PROFILE_CLOUD_DRIVE));
export const hasReauthorizePatientProfileCloudDriveErrors = createSelector(errorsSelector, (state)=>includes(state, actionTypes.REAUTHORIZE_PATIENT_PROFILE_CLOUD_DRIVE));
//----------------------------------------------------------------------------------------------------------------------
export const isConnectToClinicInProgress = createSelector(sendingSelector, (state)=>includes(state, actionTypes.CONNECT_TO_CLINIC));
export const hasConnectToClinicErrors = createSelector(errorsSelector, (state)=>includes(state, actionTypes.CONNECT_TO_CLINIC));
//----------------------------------------------------------------------------------------------------------------------
export const isFetchMembershipsInProgress = createSelector(fetchingSelector, (state)=>includes(state, actionTypes.FETCH_MEMBERSHIPS));
export const hasFetchMembershipsErrors = createSelector(errorsSelector, (state)=>includes(state, actionTypes.FETCH_MEMBERSHIPS));
//----------------------------------------------------------------------------------------------------------------------
export const isFetchAccountDumpInProgress = createSelector(fetchingSelector, (state)=>includes(state, actionTypes.GDPR_FETCH_ACCOUNT_DUMP));
export const hasFetchAccountDumpErrors = createSelector(errorsSelector, (state)=>includes(state, actionTypes.GDPR_FETCH_ACCOUNT_DUMP));
//----------------------------------------------------------------------------------------------------------------------
export const isConnectToPatientStorageInProgress = createSelector(sendingSelector, (state)=>includes(state, actionTypes.CONNECT_TO_PATIENT_STORAGE));
//----------------------------------------------------------------------------------------------------------------------
export const isFetchCountrySettingsInProgress = createSelector(fetchingSelector, (state)=>includes(state, actionTypes.FETCH_COUNTRY_SETTINGS));
export const hasFetchCountrySettingsErrors = createSelector(errorsSelector, (state)=>includes(state, actionTypes.FETCH_COUNTRY_SETTINGS));
//----------------------------------------------------------------------------------------------------------------------
export const isUpdateNotificationsSettingsInProgress = createSelector(fetchingSelector, (state)=>includes(state, actionTypes.UPDATE_NOTIFICATIONS_SETTINGS));
export const hasUpdateNotificationsSettingsErrors = createSelector(errorsSelector, (state)=>includes(state, actionTypes.UPDATE_NOTIFICATIONS_SETTINGS));
//----------------------------------------------------------------------------------------------------------------------
export const isValidateMembershipInProgress = createSelector(fetchingSelector, (state)=>includes(state, actionTypes.VALIDATE_MEMBERSHIPS));
export const hasValidateMembershipErrors = createSelector(errorsSelector, (state)=>includes(state, actionTypes.VALIDATE_MEMBERSHIPS)); //----------------------------------------------------------------------------------------------------------------------
